import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import {
  createGoodsReceipt,
  getGoodsReceiptById,
  getGoodsReceiptsPage,
  getCompanyGroupAdmins,
  getGoodsReceiptReceiptContext,
  getVesselsByCompany,
  getReceivableGoodsReceiptPurchaseOrdersPage,
} from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import {
  GoodsReceiptDetail,
  GoodsReceiptListRow,
  CreateGoodsReceiptRequest,
  GoodsReceiptCondition,
  CompanyGroupAdmin,
  Vessel,
  GoodsReceiptReceiptContext,
  GoodsReceiptReceivablePoSelectorRow,
} from '../core/_models'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import ProcurementAttachmentGallery from './ProcurementAttachmentGallery'
import {
  getContextHeaderLabels,
  getEquipmentNameFromLine,
  getLinkedDefectDisplay,
  getSubcomponentNameFromLine,
  resolveRequisitionViewContext,
  uniqueJoinedValues,
} from '../core/technicalItemContext'
import {downloadElementAsPdf} from '../core/exportUtils'
import GoodsReceiptGRNDocument from './GoodsReceiptGRNDocument'
import {buildGoodsReceiptPayload, validateGoodsReceiptLines} from '../core/goodsReceiptValidation'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {useNavigate} from 'react-router-dom'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'

const toProcurementListFilters = (globalState: {
  company: {id: number; type: string} | null
  vessel: {id?: number | null} | null
}) => ({
  ...(globalState.company?.id ? {companyId: globalState.company.id, companyType: globalState.company.type} : {}),
  ...(globalState.vessel?.id ? {vesselId: Number(globalState.vessel.id)} : {}),
})

type ReceiptItem = {
  requisitionLineId?: number | null
  inventoryItemId?: number | null
  description: string
  partNumber: string
  expectedQty: number
  previouslyReceivedQty: number
  remainingQty: number
  currentReceivedQty: number
  condition: GoodsReceiptCondition
  notes: string
}

const normalizeGoodsReceiptAttachments = (receipt: GoodsReceiptDetail | null | undefined) =>
  ((((receipt as any)?.attachments || []).length
    ? ((receipt as any)?.attachments || [])
    : (receipt?.photos || [])) as any[]).map((attachment: any, index: number) => ({
    id: attachment?.id ?? null,
    registryId: attachment?.registryId ?? attachment?.id ?? null,
    attachmentKey: attachment?.attachmentKey ?? null,
    fileName: attachment?.fileName || `Attachment ${index + 1}`,
    url: attachment?.url || '',
    viewUrl: attachment?.viewUrl || null,
    downloadUrl: attachment?.downloadUrl || null,
    sizeBytes: attachment?.sizeBytes ?? attachment?.size ?? 0,
    available: attachment?.available ?? null,
  }))

// Helper: returns a clean inventoryId or null (for general items)
const normalizeInventoryId = (item: ReceiptItem): number | null =>
  typeof item.inventoryItemId === 'number' && !Number.isNaN(item.inventoryItemId) ? item.inventoryItemId : null

interface CreateReceiptModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
  purchaseOrderId?: number
}

export const CreateReceiptModal: FC<CreateReceiptModalProps> = ({ visible, onClose, onSubmit, purchaseOrderId }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const grnScope =
    getScope(PROCUREMENT_PERMS.CREATE_GOODS_RECEIPT) ??
    getScope(PROCUREMENT_PERMS.READ_GOODS_RECEIPT) ??
    null
  /** UX only: scope from effective permissions replaces legacy role checks for picker layout. */
  const isGlobalCatalogUx = grnScope === 'ALL'
  const isCompanyCatalogUx = grnScope === 'COMPANY'
  const isVesselScopedUx = grnScope === 'VESSEL'
  const currentUsername = useMemo(
    () =>
      String(
        (currentUser as any)?.username ||
          (currentUser as any)?.userName ||
          (currentUser as any)?.name ||
          ''
      ).trim(),
    [currentUser]
  )
  const [formData, setFormData] = useState({
    purchaseOrderId: purchaseOrderId ? purchaseOrderId.toString() : '',
    receivedDate: '',
    receivedBy: currentUsername,
    vendorRating: '',
    photos: [] as File[],
    items: [] as ReceiptItem[],
    generalNotes: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [receivablePOs, setReceivablePOs] = useState<GoodsReceiptReceivablePoSelectorRow[]>([])
  const [selectedReceiptContext, setSelectedReceiptContext] = useState<GoodsReceiptReceiptContext | null>(null)
  const [isLoadingPOs, setIsLoadingPOs] = useState(false)
  const [isLoadingReceiptContext, setIsLoadingReceiptContext] = useState(false)
  const [poSearchTerm, setPoSearchTerm] = useState('')
  const [poPage, setPoPage] = useState(0)
  const [poTotalPages, setPoTotalPages] = useState(1)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const [selectedVesselId, setSelectedVesselId] = useState<string>('')
  const crewVesselId = currentUser?.vessel?.id ?? (currentUser as any)?.vesselId ?? null
  const effectiveVesselFilter = isVesselScopedUx ? (crewVesselId ? String(crewVesselId) : '') : selectedVesselId
  const selectedPO = useMemo<GoodsReceiptReceivablePoSelectorRow | null>(() => {
    if (formData.purchaseOrderId) {
      const fromPage = receivablePOs.find(
        (po) => String(po.purchaseOrderId) === formData.purchaseOrderId
      )
      if (fromPage) {
        return fromPage
      }
    }
    if (!selectedReceiptContext) {
      return null
    }
    return {
      purchaseOrderId: selectedReceiptContext.purchaseOrderId,
      poNumber: selectedReceiptContext.poNumber,
      status: selectedReceiptContext.status,
      vesselId: selectedReceiptContext.vesselId,
      vesselName: selectedReceiptContext.vesselName,
      vendorId: selectedReceiptContext.vendorId,
      vendorName: selectedReceiptContext.vendorName,
      requisitionId: selectedReceiptContext.requisitionId,
      requisitionNo: selectedReceiptContext.requisitionNo,
      rfqId: selectedReceiptContext.rfqId,
      rfqNumber: selectedReceiptContext.rfqNumber,
      categoryName: selectedReceiptContext.categoryName,
      requisitionSubmittedDate: selectedReceiptContext.requisitionSubmittedDate,
    }
  }, [receivablePOs, formData.purchaseOrderId, selectedReceiptContext])
  const requisitionDateForSelectedPO = useMemo(() => {
    const raw = selectedReceiptContext?.requisitionSubmittedDate || null
    return raw ? String(raw).slice(0, 10) : ''
  }, [selectedReceiptContext])
  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), [])

  useEffect(() => {
    if (visible) {
      setFormData((prev) => ({
        ...prev,
        receivedBy: prev.receivedBy || currentUsername,
      }))
      initDropdowns()
    }
  }, [visible, currentUsername])

  // Update form data when purchaseOrderId prop changes
  useEffect(() => {
    if (purchaseOrderId) {
      setFormData((prev) => ({
        ...prev,
        purchaseOrderId: purchaseOrderId.toString(),
      }))
    }
  }, [purchaseOrderId])

  useEffect(() => {
    if (!visible || purchaseOrderId) {
      return
    }
    if ((isGlobalCatalogUx || isCompanyCatalogUx) && !effectiveVesselFilter) {
      setReceivablePOs([])
      setPoTotalPages(1)
      if (formData.purchaseOrderId) {
        setFormData((prev) => ({...prev, purchaseOrderId: '', items: []}))
        setSelectedReceiptContext(null)
      }
      return
    }
    void loadReceivablePurchaseOrders()
  }, [
    visible,
    purchaseOrderId,
    poPage,
    poSearchTerm,
    effectiveVesselFilter,
    isGlobalCatalogUx,
    isCompanyCatalogUx,
    formData.purchaseOrderId,
  ])

  useEffect(() => {
    if (!visible || !formData.purchaseOrderId || formData.items.length > 0 || selectedReceiptContext) {
      return
    }
    void hydrateSelectedPurchaseOrder(formData.purchaseOrderId)
  }, [visible, formData.purchaseOrderId, formData.items.length, selectedReceiptContext])

  const loadReceivablePurchaseOrders = async () => {
    setIsLoadingPOs(true)
    try {
      const page = await getReceivableGoodsReceiptPurchaseOrdersPage(
        poPage,
        25,
        poSearchTerm.trim() || undefined,
        effectiveVesselFilter ? Number(effectiveVesselFilter) : undefined
      )
      setReceivablePOs(page.content || [])
      setPoTotalPages(page.totalPages || 1)

      if (
        formData.purchaseOrderId &&
        !page.content.some((po) => String(po.purchaseOrderId) === formData.purchaseOrderId) &&
        !purchaseOrderId
      ) {
        setFormData((prev) => ({...prev, purchaseOrderId: '', items: []}))
        setSelectedReceiptContext(null)
      }
    } catch (error: any) {
      console.error('Error loading receivable purchase orders:', error)
      toast.error('Failed to load purchase orders')
    } finally {
      setIsLoadingPOs(false)
    }
  }

  const hydrateSelectedPurchaseOrder = useCallback(async (value: string) => {
    if (!value) {
      setSelectedReceiptContext(null)
      setFormData((prev) => ({...prev, purchaseOrderId: '', items: []}))
      return
    }
    setIsLoadingReceiptContext(true)
    try {
      const context = await getGoodsReceiptReceiptContext(parseInt(value, 10))
      const mapped: ReceiptItem[] = (context.lines || []).map((line) => ({
        requisitionLineId: line.requisitionLineId ?? null,
        inventoryItemId: line.inventoryItemId ?? null,
        description: line.itemDescription,
        partNumber: line.partNumber || '',
        expectedQty: line.expectedQty,
        previouslyReceivedQty: line.receivedQty,
        remainingQty: line.remainingQty,
        currentReceivedQty: 0,
        condition: 'GOOD',
        notes: '',
      }))
      setSelectedReceiptContext(context)
      setFormData((prev) => ({...prev, purchaseOrderId: value, items: mapped}))
    } catch (error: any) {
      console.error('Error loading goods receipt purchase order context:', error)
      toast.error('Failed to load purchase order receipt context')
    } finally {
      setIsLoadingReceiptContext(false)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (name === 'purchaseOrderId') {
      void hydrateSelectedPurchaseOrder(value)
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleItemChange = (index: number, field: keyof ReceiptItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item
        if (field === 'currentReceivedQty') {
          const raw = String(value ?? '').trim()
          if (!raw) {
            return { ...item, currentReceivedQty: 0 }
          }
          if (!/^\d+$/.test(raw)) {
            return { ...item, currentReceivedQty: Number(raw) }
          }
          const parsed = Number.parseInt(raw, 10)
          return { ...item, currentReceivedQty: Number.isNaN(parsed) ? 0 : parsed }
        }
        return { ...item, [field]: value }
      })
    }))
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...Array.from(e.target.files!)]
      }))
    }
  }

  const handleSubmit = async () => {
    if (!formData.purchaseOrderId) {
      toast.error('Please select a purchase order')
      return
    }
    if (!formData.receivedDate || !formData.receivedBy) {
      toast.error('Please fill in all required fields')
      return
    }
    const vendorRating = Number(formData.vendorRating)
    if (!Number.isInteger(vendorRating) || vendorRating < 1 || vendorRating > 5) {
      toast.error('Vendor rating must be an integer from 1 to 5')
      return
    }
    if (requisitionDateForSelectedPO && formData.receivedDate < requisitionDateForSelectedPO) {
      toast.error('Goods Receipt date cannot be before Requisition date')
      return
    }
    if (formData.receivedDate > todayDate) {
      toast.error('Goods Receipt date cannot be in the future')
      return
    }
    const validation = validateGoodsReceiptLines(formData.items)
    if (validation.rowError) {
      toast.error(validation.rowError)
      return
    }
    if (validation.receiptError) {
      toast.error(validation.receiptError)
      return
    }
    setIsSubmitting(true)
    try {
      const payload: CreateGoodsReceiptRequest = buildGoodsReceiptPayload(
        parseInt(formData.purchaseOrderId),
        formData.receivedDate,
        formData.receivedBy,
        vendorRating,
        formData.generalNotes,
        formData.items.map((item) => ({
          ...item,
          inventoryItemId: normalizeInventoryId(item),
        }))
      )

      const created = await createGoodsReceipt(payload, formData.photos)
      const resultingPoStatus = String((created as any)?.purchaseOrderStatus || selectedPO?.status || '').toUpperCase()
      toast.success(
        resultingPoStatus === 'DELIVERED'
          ? 'Goods receipt created successfully. Purchase Order is fully delivered.'
          : 'Goods receipt created successfully. Purchase Order remains partially delivered.'
      )
      handleClose()
      onSubmit()
    } catch (error: any) {
      console.error('Error creating goods receipt:', error)
      toast.error('Failed to create goods receipt')
    } finally {
      setIsSubmitting(false)
    }
  }

  const initDropdowns = async () => {
    try {
      if (isGlobalCatalogUx) {
        const list = await getCompanyGroupAdmins()
        setCompanies(list)
      } else if (isCompanyCatalogUx) {
        const companyUsername = (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
        if (companyUsername) {
          const vs = await getVesselsByCompany(companyUsername)
          setVessels(vs)
        }
      } else if (isVesselScopedUx) {
        setSelectedVesselId(crewVesselId ? String(crewVesselId) : '')
      }
    } catch (err: any) {
      console.error('Error loading dropdown data:', err)
    }
  }

  const handleCompanyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const companyId = e.target.value
    setSelectedCompanyId(companyId)
    setSelectedVesselId('')
    setPoPage(0)
    setSelectedReceiptContext(null)
    setReceivablePOs([])
    setFormData(prev => ({ ...prev, purchaseOrderId: '', items: [] }))
    setVessels([])
    try {
      const company = companies.find(c => String(c.id) === companyId)
      const username = company?.username || company?.name || ''
      if (username) {
        const vs = await getVesselsByCompany(username)
        setVessels(vs)
      }
    } catch (err: any) {
      console.error('Error loading vessels for company:', err)
    }
  }

  const handleVesselChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vesselId = e.target.value
    setSelectedVesselId(vesselId)
    setPoPage(0)
    setSelectedReceiptContext(null)
    setReceivablePOs([])
    setFormData(prev => ({ ...prev, purchaseOrderId: '', items: [] }))
  }

  const handleClose = () => {
    setFormData({
      purchaseOrderId: purchaseOrderId ? purchaseOrderId.toString() : '',
      receivedDate: '',
      receivedBy: currentUsername,
      vendorRating: '',
      photos: [],
      items: [],
      generalNotes: ''
    })
    setReceivablePOs([])
    setSelectedReceiptContext(null)
    setPoSearchTerm('')
    setPoPage(0)
    setPoTotalPages(1)
    onClose()
  }

  if (!visible) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '60rem', overflowY: "scroll" }}>
        <div className="custom-modal-header d-flex justify-content-between align-items-center">
          <div>
            <h5 className='m-0'>Create Goods Receipt</h5>
            <p className='text-muted mb-0 fs-7'>Enter goods receipt details</p>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
          </button>
        </div>

        <div className="custom-modal-body">
          <div className='row g-3 mb-5'>
            {/* Superadmin: choose company & vessel; Company user: choose vessel */}
            {isGlobalCatalogUx && (
              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Company</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                >
                  <option value=''>All Companies</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name || c.username || `Company ${c.id}`}</option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            {(isGlobalCatalogUx || isCompanyCatalogUx) && (
              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vessel</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedVesselId}
                  onChange={handleVesselChange}
                >
                  <option value=''>Select Vessel</option>
                  {vessels.map(v => (
                    <option key={v.id} value={v.id}>{v.fleet_name || (v as any).fleetName || `Vessel ${v.id}`}</option>
                  ))}
                </SearchableSelect>
              </div>
            )}

            <div className='col-md-3'>
              <label htmlFor='goods-receipt-po' className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Purchase Order</label>
              <input
                type='text'
                className='form-control mb-2'
                placeholder='Search PO / vendor / requisition'
                value={poSearchTerm}
                onChange={(e) => {
                  setPoSearchTerm(e.target.value)
                  setPoPage(0)
                }}
                disabled={!!purchaseOrderId || ((isGlobalCatalogUx || isCompanyCatalogUx) && !effectiveVesselFilter)}
              />
              {isLoadingPOs ? (
                <div className='form-control d-flex align-items-center'>
                  <div className='spinner-border spinner-border-sm me-2' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  Loading POs...
                </div>
              ) : (
                <>
                  <SearchableSelect
                    id='goods-receipt-po'
                    className='form-select'
                    name='purchaseOrderId'
                    value={formData.purchaseOrderId}
                    onChange={handleInputChange}
                    style={{ color: '#000' }}
                    disabled={!!purchaseOrderId || isLoadingReceiptContext}
                  >
                    <option value=''>Select Purchase Order</option>
                    {selectedPO && !receivablePOs.some((po) => po.purchaseOrderId === selectedPO.purchaseOrderId) && (
                      <option key={selectedPO.purchaseOrderId} value={selectedPO.purchaseOrderId.toString()}>
                        PO-{selectedPO.purchaseOrderId} - {selectedPO.poNumber || `PO${selectedPO.purchaseOrderId}`} | Req: {selectedPO.requisitionNo || '-'} | {selectedPO.vendorName || 'Unknown Vendor'} | Remaining: {selectedReceiptContext?.lines?.reduce((sum, line) => sum + (line.remainingQty || 0), 0) ?? 0} | {selectedPO.status || 'Unknown Status'}
                      </option>
                    )}
                    {receivablePOs.map((po) => (
                      <option key={po.purchaseOrderId} value={po.purchaseOrderId.toString()}>
                        PO-{po.purchaseOrderId} - {po.poNumber || `PO${po.purchaseOrderId}`} | Req: {po.requisitionNo || '-'} | {po.vendorName || 'Unknown Vendor'} | Remaining: {po.totalRemainingQty ?? 0} | {po.status || 'Unknown Status'}
                      </option>
                    ))}
                  </SearchableSelect>
                  {receivablePOs.length === 0 && !isLoadingPOs && !isLoadingReceiptContext && (
                    <small className='text-muted'>
                      {(isGlobalCatalogUx || isCompanyCatalogUx) && !effectiveVesselFilter
                        ? 'Select a vessel to load eligible purchase orders.'
                        : 'No receivable purchase orders available.'}
                    </small>
                  )}
                  {isLoadingReceiptContext && (
                    <small className='text-muted'>Loading purchase order receipt context...</small>
                  )}
                  {receivablePOs.length > 0 && (
                    <div className='d-flex justify-content-between align-items-center mt-2'>
                      <small className='text-muted'>Format: PO-ID - PO Number | Req | Vendor | Remaining | Status</small>
                      <div className='d-flex align-items-center gap-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          disabled={poPage <= 0}
                          onClick={() => setPoPage((prev) => Math.max(prev - 1, 0))}
                        >
                          Prev
                        </button>
                        <small className='text-muted'>
                          Page {poPage + 1} / {Math.max(poTotalPages, 1)}
                        </small>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          disabled={poPage + 1 >= poTotalPages}
                          onClick={() => setPoPage((prev) => prev + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Requisition Number</label>
              <input
                className='form-control'
                value={selectedReceiptContext?.requisitionNo || selectedPO?.requisitionNo || ''}
                readOnly
              />
            </div>

            <div className='col-md-3'>
              <label htmlFor='goods-receipt-date' className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Received Date</label>
              <input
                id='goods-receipt-date'
                type='date'
                className='form-control'
                name='receivedDate'
                value={formData.receivedDate}
                min={requisitionDateForSelectedPO || undefined}
                max={todayDate}
                onChange={handleInputChange}
                style={{ color: '#000' }}
              />
            </div>

            <div className='col-md-3'>
              <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Received By</label>
              <input
                type='text'
                className='form-control'
                name='receivedBy'
                value={formData.receivedBy}
                onChange={handleInputChange}
                placeholder='anthani835'
                style={{ color: '#000' }}
              />
            </div>

            <div className='col-md-3'>
              <label htmlFor='goods-receipt-vendor-rating' className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Vendor Rating</label>
              <select
                id='goods-receipt-vendor-rating'
                className='form-select'
                name='vendorRating'
                value={formData.vendorRating}
                onChange={handleInputChange}
                style={{ color: '#000' }}
              >
                <option value=''>Select rating</option>
                {[1, 2, 3, 4, 5].map((rating) => (
                  <option key={rating} value={rating}>{rating} / 5</option>
                ))}
              </select>
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Attachments (Photos/PDFs)</label>
              <div className='mb-2'>
                <input
                  type='file'
                  className='form-control'
                  multiple
                  accept='image/*,application/pdf'
                  onChange={handlePhotoChange}
                />
              </div>
              {formData.photos.length > 0 && (
                <small className='text-muted'>{formData.photos.length} file(s) selected</small>
              )}
            </div>
          </div>

          <div className='mb-4'>
            <h6 className='fw-bold mb-3' style={{ color: '#181C32' }}>Item Verification</h6>

            <div style={{ overflowX: 'visible' }}>
              <table className='table table-row-bordered align-middle' style={{ fontSize: '14px', width: '100%' }}>
                <thead style={{ backgroundColor: '#F5F8FA' }}>
                  <tr>
                    <th className='fw-semibold text-uppercase' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '22%' }}>Item Description</th>
                    <th className='fw-semibold text-uppercase text-center' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '10%' }}>Expected</th>
                    <th className='fw-semibold text-uppercase text-center' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '10%' }}>Remaining</th>
                    <th className='fw-semibold text-uppercase text-center' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '10%' }}>Already Received</th>
                    <th className='fw-semibold text-uppercase text-center' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '15%' }}>This Receipt</th>
                    <th className='fw-semibold text-uppercase' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '18%' }}>Condition</th>
                    <th className='fw-semibold text-uppercase' style={{ color: '#7E8299', fontSize: '12px', padding: '12px', width: '15%' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td style={{ padding: '12px', wordBreak: 'break-word' }}>
                        <div className='fw-semibold text-dark'>{item.description}</div>
                        <div className='text-muted' style={{ fontSize: '12px' }}>{item.partNumber}</div>
                      </td>
                      <td className='text-center' style={{ padding: '12px' }}>
                        <span className='fw-semibold'>{item.expectedQty}</span>
                      </td>
                      <td className='text-center' style={{ padding: '12px' }}>
                        <span className='fw-semibold text-primary'>
                          {Math.max(item.remainingQty, 0)}
                        </span>
                      </td>
                      <td className='text-center' style={{ padding: '12px' }}>
                        <span className='fw-semibold text-muted'>{Math.max(item.previouslyReceivedQty, 0)}</span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <input
                          type='number'
                          className='form-control form-control-sm text-center'
                          value={item.currentReceivedQty}
                          min={0}
                          max={Math.max(item.remainingQty, 0)}
                          step={1}
                          disabled={Math.max(item.remainingQty, 0) <= 0}
                          onChange={(e) => handleItemChange(index, 'currentReceivedQty', e.target.value)}
                          style={{ width: '100%', margin: '0 auto' }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <SearchableSelect
                          className='form-select form-select-sm'
                          value={item.condition}
                          disabled={Math.max(item.remainingQty, 0) <= 0}
                          onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                          style={{ width: '100%' }}
                        >
                          <option value='GOOD'>Good</option>
                          <option value='DAMAGED'>Damaged</option>
                          <option value='MISSING'>Missing</option>
                        </SearchableSelect>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <input
                          type='text'
                          className='form-control form-control-sm'
                          placeholder='Notes...'
                          value={item.notes}
                          disabled={Math.max(item.remainingQty, 0) <= 0}
                          onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>General Notes</label>
            <textarea
              className='form-control'
              name='generalNotes'
              value={formData.generalNotes}
              onChange={handleInputChange}
              rows={3}
              placeholder='Overall condition, packaging notes, certificate verification, etc.'
              style={{ color: '#000' }}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn_secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            className="btn btn_primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Complete Receipt'}
          </button>
        </div>
      </div>
    </div>
  )
}



const GoodsReceipt: FC = () => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { state: globalState } = useGlobalFilter()
  const globalCompanyId = globalState.company?.id ?? null
  const globalCompanyType = globalState.company?.type ?? null
  const globalVesselId = globalState.vessel?.id ? Number(globalState.vessel.id) : null
  const {
    can: canProcurementPermission,
    loading: procurementPermissionsLoading,
  } = useProcurementPermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [receiptData, setReceiptData] = useState<GoodsReceiptListRow[]>([])
  const [serverTotalElements, setServerTotalElements] = useState(0)
  const [serverTotalPages, setServerTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof GoodsReceiptListRow | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [viewReceipt, setViewReceipt] = useState<GoodsReceiptDetail | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [receiptDownloadDoc, setReceiptDownloadDoc] = useState<{
    receipt: GoodsReceiptDetail
  } | null>(null)
  const openedReceiptIdRef = useRef<number | null>(null)

  const canViewReceipt = canProcurementPermission(PROCUREMENT_PERMS.READ_GOODS_RECEIPT)
  const canCreateReceipt = canProcurementPermission(PROCUREMENT_PERMS.CREATE_GOODS_RECEIPT)
  const canDownloadReceipt =
    canProcurementPermission(PROCUREMENT_PERMS.GRN_DOWNLOAD) ||
    canProcurementPermission(PROCUREMENT_PERMS.READ_GOODS_RECEIPT)

  const loadGoodsReceipts = useCallback(async () => {
    setIsLoading(true)
    try {
      const pageIndex = Math.max(0, currentPage - 1)
      const data = await getGoodsReceiptsPage(
        pageIndex,
        rowsPerPage,
        toProcurementListFilters(globalState)
      )
      const content = data.content || []
      
      console.log('Goods receipts loaded:', content.length)
      setReceiptData(content)
      setServerTotalElements(data.totalElements ?? content.length)
      setServerTotalPages(data.totalPages ?? 1)
    } catch (error: any) {
      console.error('Error loading goods receipts:', error)
      toast.error('Failed to load goods receipts')
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, rowsPerPage, globalCompanyId, globalCompanyType, globalVesselId])

  // Load goods receipts on component mount or when pagination changes
  useEffect(() => {
    loadGoodsReceipts()
  }, [loadGoodsReceipts])

  useEffect(() => {
    setCurrentPage(1)
  }, [globalCompanyId, globalCompanyType, globalVesselId])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const receiptId = Number(urlParams.get('goodsReceiptId') || 0)
    if (!Number.isFinite(receiptId) || receiptId <= 0 || openedReceiptIdRef.current === receiptId) {
      return
    }
    openedReceiptIdRef.current = receiptId
    void handleViewReceipt({id: receiptId} as GoodsReceiptListRow)
  }, [currentPage, rowsPerPage])

  useEffect(() => {
    if (!isViewModalVisible && openedReceiptIdRef.current != null) {
      const params = new URLSearchParams(window.location.search)
      if (params.get('goodsReceiptId')) {
        params.delete('goodsReceiptId')
        navigate({search: params.toString() ? `?${params.toString()}` : ''}, {replace: true})
      }
      openedReceiptIdRef.current = null
    }
  }, [isViewModalVisible, navigate])

  // Calculate stats based on status
  const completedReceipts = receiptData.filter(r => r.status === 'POSTED' || r.status === 'COMPLETED').length
  const pendingReceipts = receiptData.filter(r => r.status === 'PENDING' || r.status === 'DRAFT').length
  const discrepancies = receiptData.filter(r => Number(r.discrepancyCount || 0) > 0).length

  // Calculate total items received
  const totalItemsReceived = receiptData.reduce(
    (sum, r) => sum + Number(r.totalReceivedQty || 0),
    0
  )

  const handleViewReceipt = async (record: GoodsReceiptListRow) => {
    setIsViewModalVisible(true)
    setViewReceipt(null)
    setViewLoading(true)
    try {
      const receipt = await getGoodsReceiptById(record.id)
      setViewReceipt(receipt)
    } catch (error) {
      console.error('Error loading goods receipt view context:', error)
      toast.warning('Failed to load goods receipt details.')
      setIsViewModalVisible(false)
      setViewReceipt(null)
    } finally {
      setViewLoading(false)
    }
  }

  const handleDownloadReceipt = async (
    recordOrId: GoodsReceiptListRow | GoodsReceiptType | GoodsReceiptDetail | number,
    options?: {silent?: boolean}
  ) => {
    if (!canDownloadReceipt) {
      if (!options?.silent) toast.error('You do not have goods receipt download access.')
      return
    }
    const receipt =
      typeof recordOrId === 'number'
        ? await getGoodsReceiptById(recordOrId)
        : 'lines' in recordOrId
        ? recordOrId
        : await getGoodsReceiptById(recordOrId.id)
    try {
      setReceiptDownloadDoc({receipt})
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf('#goods-receipt-download-host', `Goods_Receipt_${receipt.id}`)
      if (!options?.silent) {
        toast.success('Goods Receipt PDF downloaded')
      }
    } catch (error) {
      console.error('Failed to download goods receipt PDF', error)
      if (!options?.silent) {
        toast.error('Failed to download goods receipt PDF')
      }
      throw error
    } finally {
      setReceiptDownloadDoc(null)
    }
  }

  const filteredData = useMemo(() => {
    return receiptData.filter(record => {
      const matchesSearch = searchTerm === '' ||
        record.id.toString().includes(searchTerm) ||
        record.purchaseOrderId.toString().includes(searchTerm) ||
        String(record.purchaseOrderNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(record.requisitionNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.receivedBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(record.purchaseOrderStatus || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(record.status || '').toLowerCase().includes(searchTerm.toLowerCase())

      return matchesSearch
    })
  }, [searchTerm, receiptData])

  const sortedData = useMemo(() => {
    let sortedRecords = [...filteredData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [filteredData, sortConfig])

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'POSTED': 'bg-success',
      'COMPLETED': 'bg-success',
      'PENDING': 'bg-warning',
      'DRAFT': 'bg-secondary',
      'DISCREPANCY': 'bg-danger'
    }
    const color = statusColors[status] || 'bg-secondary'
    return (
      <span className={`badge ${color} text-white`}>
        {status}
      </span>
    )
  }

  const handleSort = (key: keyof GoodsReceiptListRow) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const filteredTotalElements = serverTotalElements || sortedData.length
  const currentRecords = sortedData
  const totalPages = serverTotalPages || Math.max(1, Math.ceil(filteredTotalElements / rowsPerPage))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const handleCreateReceipt = () => {
    setIsModalVisible(true)
  }

  const handleSubmitReceipt = () => {
    // Refresh the data after creating receipt
    loadGoodsReceipts()
  }

  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewReceipt) {
    return <div className='alert alert-warning m-6'>You do not have goods receipt view access.</div>
  }

  return (
    <>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid'>
          <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
            <div className='card'>
              <div className='card-header border-0 pt-6 bg-white'>
                <div>
                  <h3 className='card-label text-dark fw-bold'>Goods Receipt</h3>
                  <p className='text-muted mb-0'>Verify and receive delivered items</p>
                </div>
                {canCreateReceipt && (
                  <div className='card-toolbar'>
                    <button
                      type='button'
                      className='btn btn_primary'
                      onClick={handleCreateReceipt}
                    >
                      <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                      Create Goods Receipt
                    </button>
                  </div>
                )}
              </div>

              <div className='card-body py-4 bg-white border-top'>
                <div className='mb-4'>
                  <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Search receipts...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className='mb-8'>
                  <h4 className='fw-bold text-primary mb-4'>Goods Receipts ({filteredTotalElements})</h4>
                  <div className='report-table table-responsive'>
                    <div style={{ overflowX: 'auto' }}>
                      <table className='table table-bordered align-middle'>
                        <thead className='table-header text-start'>
                          <tr>
                            <th onClick={() => handleSort('id')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>GRN ID</span>
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'id' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            <th onClick={() => handleSort('purchaseOrderId')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>PO ID</span>
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'purchaseOrderId' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Requisition</th>
                            <th onClick={() => handleSort('receivedDate')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                              <div className='d-flex align-items-center'>
                                <span style={{ color: '#3F4254', fontWeight: 600 }}>Received Date</span>
                                <div style={{ transform: 'translateY(-2px)' }}>
                                  <KTSVG
                                    path={`/media/map/sort-col-${sortConfig.key === 'receivedDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                    className='svg-icon ms-2 custom-sort-icon'
                                  />
                                </div>
                              </div>
                            </th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Received By</th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Items</th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Status</th>
                            <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Attachments</th>
                            <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody className='table-body text-start'>
                          {isLoading ? (
                            <tr>
                              <td colSpan={9} className='text-center text-muted py-5'>
                                Loading goods receipts...
                              </td>
                            </tr>
                          ) : currentRecords.length === 0 ? (
                            <tr>
                              <td colSpan={9} className='text-center text-muted py-5'>
                                No receipts found for the selected criteria.
                              </td>
                            </tr>
                          ) : (
                            currentRecords.map((record) => (
                              <tr key={record.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>GRN-{record.id}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {record.purchaseOrderNumber || `PO-${record.purchaseOrderId}`}
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  <div>
                                    {record.requisitionNo ? record.requisitionNo : record.requisitionId ? `REQ-${record.requisitionId}` : '-'}
                                  </div>
                                  <small className='text-muted'>{record.requisitionTitle || '-'}</small>
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.receivedDate}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.receivedBy}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  <div>
                                    <div className='fw-semibold'>
                                      {Number(record.lineCount || 0)} line(s)
                                    </div>
                                    <div className='text-muted'>
                                      Received {Number(record.totalReceivedQty || 0)} / {Number(record.totalExpectedQty || 0)}
                                    </div>
                                    {Number(record.discrepancyCount || 0) > 0 && (
                                      <div className='text-danger'>
                                        {Number(record.discrepancyCount || 0)} discrepancy line(s)
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '12px 16px' }}>{getStatusBadge(record.status)}</td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {Number(record.attachmentCount || 0) > 0 ? (
                                    <span className='badge bg-primary'>{Number(record.attachmentCount || 0)} file(s)</span>
                                  ) : (
                                    <span className='text-muted'>No attachments</span>
                                  )}
                                </td>
                                <td className='text-center' style={{ padding: '12px 16px' }}>
                                  <button
                                    className='btn btn-sm px-0'
                                    title='View'
                                    onClick={() => {
                                      void handleViewReceipt(record)
                                    }}
                                  >
                                    <KTSVG path='/media/map/ph_eye.svg' />
                                  </button>
                                  {canDownloadReceipt && (
                                    <button
                                      className='btn btn-sm btn-primary ms-2'
                                      title='Download PDF'
                                      onClick={() => {
                                        void handleDownloadReceipt(record)
                                      }}
                                    >
                                      Download
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div
                      className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                      style={{
                        position: 'static',
                        bottom: 0,
                        backgroundColor: '#fff',
                        zIndex: 10,
                        borderTop: '1px solid #dee2e6',
                        marginTop: 'auto'
                      }}
                    >
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px'
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                          Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{Math.min(currentPage * rowsPerPage, filteredTotalElements)}</strong> of <strong>{filteredTotalElements}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none'
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px'
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px'
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateReceiptModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSubmit={handleSubmitReceipt}
      />
      {isViewModalVisible && (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', position: 'fixed', inset: 0, zIndex: 1050 }}
        >
          <div className='modal-dialog modal-lg modal-dialog-centered procurement-view-modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <div>
                  <h3 className='modal-title fw-bold mb-0'>Goods Receipt Details</h3>
                  <p className='text-muted mb-0 fs-7'>
                    {viewReceipt ? `GRN-${viewReceipt.id} | PO-${viewReceipt.purchaseOrderId}` : 'Loading goods receipt...'}
                  </p>
                </div>
                <button type='button' className='btn-close' onClick={() => { setIsViewModalVisible(false); setViewReceipt(null) }}></button>
              </div>
              <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {viewLoading || !viewReceipt ? (
                  <div className='d-flex align-items-center justify-content-center py-8 text-muted'>
                    Loading goods receipt details...
                  </div>
                ) : (
                (() => {
                  const requestFor = viewReceipt.requestFor
                  const categoryName = viewReceipt.categoryName || '-'
                  const context = resolveRequisitionViewContext(requestFor, categoryName)
                  const isTechnical = context === 'TECHNICAL'
                  const {primary: contextPrimaryLabel, secondary: contextSecondaryLabel} =
                    getContextHeaderLabels(context)
                  const headName = viewReceipt.inventoryItemHeadName || '-'
                  const subHeadName = viewReceipt.inventoryItemSubHeadName || '-'
                  const subCategoryName =
                    viewReceipt.inventoryItemSubCategoryName ||
                    viewReceipt.inventoryItemSubHeadName ||
                    '-'
                  const vesselName = viewReceipt.vesselName || '-'
                  const companyName = viewReceipt.companyName || '-'
                  const linkedDefectDisplay = getLinkedDefectDisplay(viewReceipt) || '-'
                  const equipmentSummary = uniqueJoinedValues(
                    viewReceipt.lines.map((line: any) => getEquipmentNameFromLine(line))
                  )
                  const subcomponentSummary = uniqueJoinedValues(
                    viewReceipt.lines.map((line: any) => getSubcomponentNameFromLine(line))
                  )
                  const contextPrimaryValue = isTechnical
                    ? equipmentSummary
                    : context === 'SERVICES'
                    ? headName
                    : categoryName
                  const contextSecondaryValue = isTechnical
                    ? subcomponentSummary
                    : context === 'SERVICES'
                    ? subHeadName
                    : subCategoryName

                  return (
                    <>
                      <div className='row g-3 mb-4'>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Received Date</label>
                          <div className='fw-bold text-dark'>{viewReceipt.receivedDate}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Received By</label>
                          <div className='fw-bold text-dark'>{viewReceipt.receivedBy || '-'}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Vendor Rating</label>
                          <div className='fw-bold text-dark'>{viewReceipt.vendorRating == null ? '-' : `${viewReceipt.vendorRating} / 5`}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Status</label>
                          <div>{getStatusBadge(viewReceipt.status)}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Company</label>
                          <div className='fw-bold text-dark'>{companyName}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Vessel</label>
                          <div className='fw-bold text-dark'>
                            <VesselLandingLink
                              vesselId={
                                Number(
                                  viewReceipt.vesselId ??
                                    0
                                ) || null
                              }
                              label={vesselName}
                              className='text-dark text-decoration-underline'
                            />
                          </div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Requisition</label>
                          <div className='fw-bold text-dark'>
                            <div>{viewReceipt.requisitionNo || '-'}</div>
                            <div className='text-muted fw-normal'>{viewReceipt.requisitionTitle || '-'}</div>
                          </div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>Linked Defect</label>
                          <div className='fw-bold text-dark'>{linkedDefectDisplay}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>
                            {contextPrimaryLabel}
                          </label>
                          <div className='fw-bold text-dark'>{contextPrimaryValue}</div>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label text-muted fw-semibold fs-7'>
                            {contextSecondaryLabel}
                          </label>
                          <div className='fw-bold text-dark'>{contextSecondaryValue}</div>
                        </div>
                        <div className='col-md-12'>
                          <label className='form-label text-muted fw-semibold fs-7'>General Notes</label>
                          <div className='text-dark'>{viewReceipt.generalNotes || '-'}</div>
                        </div>
                      </div>

                      <h6 className='fw-bold text-primary mt-3 mb-2'>Line Items</h6>
                      <div className='table-responsive mb-3'>
                        <table className='table table-sm table-bordered align-middle'>
                          <thead className='table-light'>
                            <tr>
                              <th>#</th>
                              <th>Item</th>
                              {isTechnical ? (
                                <>
                                  <th>{contextPrimaryLabel}</th>
                                  <th>{contextSecondaryLabel}</th>
                                  <th>Part</th>
                                  <th>Part Number</th>
                                  <th>Drawing Number</th>
                                </>
                              ) : (
                                <>
                                  <th>{contextPrimaryLabel}</th>
                                  <th>{contextSecondaryLabel}</th>
                                </>
                              )}
                              <th>Expected</th>
                              <th>Received</th>
                              <th>Condition</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewReceipt.lines.map((line, idx) => {
                              const equipmentName = getEquipmentNameFromLine(line)
                              const subcomponentName = getSubcomponentNameFromLine(line)
                              const partName = line.partName || line.itemDescription || '-'
                              const partNo = line.partNumber || '-'
                              const drawingNo = line.drawingNo || '-'
                              return (
                                <tr key={idx}>
                                  <td>{idx + 1}</td>
                                  <td>{line.itemDescription || '-'}</td>
                                  {isTechnical ? (
                                    <>
                                      <td>{equipmentName || '-'}</td>
                                      <td>{subcomponentName || '-'}</td>
                                      <td>{partName || '-'}</td>
                                      <td>{partNo || '-'}</td>
                                      <td>{drawingNo || '-'}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td>{contextPrimaryValue}</td>
                                      <td>{contextSecondaryValue}</td>
                                    </>
                                  )}
                                  <td>{line.expectedQty}</td>
                                  <td>{line.receivedQty}</td>
                                  <td>{line.condition || '-'}</td>
                                  <td>{line.notes || '-'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>

                      <h6 className='fw-bold text-primary mt-3 mb-2'>Attachments</h6>
                      <ProcurementAttachmentGallery
                        attachments={normalizeGoodsReceiptAttachments(viewReceipt)}
                        emptyText='No attachments uploaded.'
                      />
                    </>
                  )
                })()
                )}
              </div>
              <div className='modal-footer'>
                {canDownloadReceipt && (
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={() => {
                      if (!viewReceipt) return
                      void handleDownloadReceipt(viewReceipt)
                    }}
                  >
                    Download PDF
                  </button>
                )}
                <button type='button' className='btn btn-light' onClick={() => { setIsViewModalVisible(false); setViewReceipt(null) }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {receiptDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='goods-receipt-download-host'>
            <GoodsReceiptGRNDocument
              receipt={receiptDownloadDoc.receipt}
            />
          </div>
        </div>
      )}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </>
  )
}

export default GoodsReceipt
